import { Component, inject, OnInit, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MegaMenuItem } from 'primeng/api';
import { Router, NavigationEnd } from '@angular/router';
import { filter, pairwise, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CurrentUserService } from '../../shared/services/current-user.service';
import { OnboardingService } from '../../shared/services/onboarding.service';
import { UpdateNoteService } from '../../update-notes/services/update-note.service';
import { JourneyService } from '../../shared/services/journey.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  standalone: false,
})
export class MenuComponent implements OnInit {
  private currentUserService = inject(CurrentUserService);
  private onboardingService = inject(OnboardingService);
  private updateNoteService = inject(UpdateNoteService);
  private journeyService = inject(JourneyService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private onboardingVisible$ = toObservable(this.onboardingService.visible);

  // Effect créé dans le contexte d'injection (doit être stocké pour éviter le garbage collection)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private readonly superAdminEffect = effect(() => {
    if (this.currentUserService.isSuperAdmin()) {
      this.addAdminMenuItem();
    }
  });

  ngOnInit() {
    // Charger les informations utilisateur
    this.currentUserService.loadCurrentUser().subscribe({
      next: () => this.promptOnboardingThenUpdates(),
    });

    // Scroller .main-content vers le haut à chaque navigation
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
          mainContent.scrollTo({ top: 0, behavior: 'auto' });
        }
      });
  }

  /**
   * Opens the onboarding popup on arrival when the journey is unfinished.
   *
   * The journey is loaded first because it is what the popup shows, and what says whether it
   * may open at all. `load()` swallows its own errors, so a failed load simply leaves the
   * journey empty and nothing opens.
   */
  private promptOnboardingThenUpdates(): void {
    this.journeyService.loadIfNeeded().subscribe(() => {
      if (this.onboardingService.promptIfNeeded()) {
        // Les nouveautés attendent la fermeture de l'onboarding : deux modales ne doivent
        // jamais se superposer. On ne peut pas simplement reporter à la session suivante, car
        // le popup revient tant que le parcours n'est pas terminé et que l'utilisateur n'a pas
        // coché « ne plus montrer » — les nouveautés seraient masquées indéfiniment.
        this.promptUpdatesAfterOnboarding();
        return;
      }

      this.updateNoteService.checkUnseenAndPrompt();
    });
  }

  /** Waits for the onboarding modal to go from open to closed, then prompts the updates. */
  private promptUpdatesAfterOnboarding(): void {
    this.onboardingVisible$
      .pipe(
        pairwise(),
        filter(([wasVisible, isVisible]) => wasVisible && !isVisible),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.updateNoteService.checkUnseenAndPrompt());
  }

  items: MegaMenuItem[] = [
    {
      label: 'menu.impactPanel',
      icon: 'pi pi-fw pi-th-large',
      routerLink: '/home/impact-panel',
      style: environment.production ? { display: 'none' } : {},
    },
    {
      label: 'menu.travelBoard',
      icon: 'pi pi-fw pi-globe',
      routerLink: '/home/travel-board',
    },
    {
      label: 'menu.calculator',
      icon: 'pi pi-fw pi-calculator',
      routerLink: '/home/calculators',
    },
    {
      label: 'menu.resources',
      icon: 'pi pi-fw pi-folder-open',
      routerLink: '/home/ressources',
      style: environment.production ? { display: 'none' } : {},
    },
    // {
    //   label: 'menu.account',
    //   icon: 'pi pi-fw pi-user',
    //   routerLink: '/home/account',
    //   style: { display: 'none' },
    // },
  ];

  private addAdminMenuItem() {
    // Éviter d'ajouter l'élément plusieurs fois
    const adminExists = this.items.some((item) => item.routerLink === '/admin');
    if (!adminExists) {
      this.items.push({
        label: 'admin page',
        icon: 'pi pi-fw pi-user-edit',
        routerLink: '/admin',
      });
    }
  }
}
